import type { SelectChangeEvent } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import TextList from '@/components/dataDisplay/TextList'
import CustomMultipleSelectField from '@/components/inputs/CustomMultipleSelectField'
import type { SelectFieldOption } from '@/components/inputs/CustomSelectField'
import { FIELD_WIDTH } from '@/constants/layout'
import { getMarketProgramLabel } from '@/features/site/utils/revenueUtils/getMarketProgramLabel'
import { isFeeMarketProgram } from '@/features/site/utils/revenueUtils/isFeeMarketProgram'
import { sortOptionsAlphabetically } from '@/utils/sortOptionsAlphabetically'

type SiteRevenueMarketProgramFilterProps = {
  marketPrograms: string[]
  availableMarketPrograms: string[]
  onChange: (marketPrograms: string[]) => void
}

export const AUTOCOMPLETE_FIELD_MAX_WIDTH = 350

function SiteRevenueMarketProgramFilter({
  marketPrograms,
  availableMarketPrograms,
  onChange,
}: Readonly<SiteRevenueMarketProgramFilterProps>) {
  const { t } = useTranslation()
  // Fee programs are always listed at the end; both groups are sorted alphabetically
  const sortedMarketProgramsOptions: SelectFieldOption<string>[] = useMemo(() => {
    const toOption = (marketProgram: string): SelectFieldOption<string> => ({
      id: marketProgram,
      label: getMarketProgramLabel(t, marketProgram),
      value: marketProgram,
    })

    return [
      ...sortOptionsAlphabetically<string>(
        availableMarketPrograms.filter((marketProgram) => !isFeeMarketProgram(marketProgram)).map(toOption),
      ),
      ...sortOptionsAlphabetically<string>(
        availableMarketPrograms.filter((marketProgram) => isFeeMarketProgram(marketProgram)).map(toOption),
      ),
    ]
  }, [t, availableMarketPrograms])

  const hasFeeMarketPrograms = availableMarketPrograms.some((marketProgram) => isFeeMarketProgram(marketProgram))
  const filterLabel = hasFeeMarketPrograms
    ? t('common.market_program.label_plural_with_fees')
    : t('common.market_program.label_plural')

  const hasAllMarketPrograms = marketPrograms.length === availableMarketPrograms.length

  function handleChange(evt: SelectChangeEvent<string[]>) {
    const values = evt.target.value

    if (values.includes('all')) {
      onChange(availableMarketPrograms)

      return
    }

    onChange(values as string[])
  }

  const selectAllLabel = hasFeeMarketPrograms
    ? t('customer_revenue.select_all_market_programs_with_fees')
    : t('customer_revenue.select_all_market_programs')

  const options: SelectFieldOption<string>[] = hasAllMarketPrograms
    ? sortedMarketProgramsOptions
    : [{ id: 'all', label: selectAllLabel, value: 'all' }, ...sortedMarketProgramsOptions]

  return (
    <CustomMultipleSelectField
      id="revenue-market-programs"
      label={filterLabel}
      options={options}
      renderValue={(values) => <TextList limit={1} values={values.map((value) => getMarketProgramLabel(t, value))} />}
      sx={{ minWidth: FIELD_WIDTH, maxWidth: AUTOCOMPLETE_FIELD_MAX_WIDTH }}
      value={marketPrograms}
      variant="outlinedWhite"
      onChange={handleChange}
    />
  )
}

export default SiteRevenueMarketProgramFilter
